import { useNotify, useRefresh, useTranslate } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { OffCrmCallDialog } from "../dialer/offcrm/OffCrmCallDialog"; // GAVION off-CRM call
import { dialHref } from "./dialHref";
// GAVION skip-callback
import { CallbackKindBadge } from "../callbacks/CallbackKindBadge";
import { SkipCallbackButton } from "../callbacks/SkipCallbackButton";

export type Callback = {
  id: number | string;
  name: string;
  next_callback_at: string;
  /** GAVION off-CRM call: the lead's zone and email for the result form (companies row). */
  timezone?: string | null;
  email?: string | null;
  /** GAVION skip-callback: « Demandé » (talked_callback) or « Auto » (companies_summary.last_call_outcome). */
  last_call_outcome?: string | null;
};
const timeFmt = new Intl.DateTimeFormat("fr-CA", {
  timeZone: "America/Toronto",
  hour: "2-digit",
  minute: "2-digit",
});

/**
 * "Mes rappels d'aujourd'hui" (overdue first, in red with a text label) with one-click Appeler; GAVION off-CRM call:
 * « Fait hors CRM » logs a callback made from a cell (same window as the lead page's « ... » menu). GAVION skip-callback:
 * each callback says « Demandé » or « Auto »; « Sauter le rappel » removes it (with a reason).
 */
export const CallbacksToday = ({
  callbacks,
  now,
}: {
  callbacks: readonly Callback[];
  now: Date;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [offCrm, setOffCrm] = useState<Callback | null>(null);
  return (
    <section
      aria-labelledby="gavion-callbacks"
      className="rounded-xl border bg-card p-4"
    >
      <h2 id="gavion-callbacks" className="mb-3 text-lg font-bold">
        {translate("gavion.dashboard.callbacks_title")}
      </h2>
      {callbacks.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("gavion.dashboard.no_callbacks")}
        </p>
      ) : (
        <ul className="divide-y">
          {callbacks.map((c) => {
            const overdue = Date.parse(c.next_callback_at) < now.getTime();
            return (
              <li
                key={c.id}
                className="flex items-center justify-between gap-3 py-2"
              >
                <span className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                  <span
                    className={`text-sm tabular-nums ${overdue ? "font-bold text-destructive" : "text-muted-foreground"}`}
                  >
                    {timeFmt.format(new Date(c.next_callback_at))}
                    {overdue
                      ? ` · ${translate("gavion.dashboard.overdue")}`
                      : ""}
                  </span>
                  <CallbackKindBadge lastCallOutcome={c.last_call_outcome} />
                  <Link
                    to={`/companies/${c.id}/show`}
                    className="truncate text-sm font-medium hover:underline"
                  >
                    {c.name}
                  </Link>
                </span>
                <span className="flex shrink-0 gap-2">
                  <SkipCallbackButton
                    companyId={c.id}
                    companyName={c.name}
                    onDone={refresh}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setOffCrm(c)}
                    aria-label={translate("gavion.dashboard.off_crm_company", {
                      name: c.name,
                    })}
                  >
                    {translate("gavion.dashboard.off_crm")}
                  </Button>
                  <Button asChild size="sm" variant="outline">
                    <Link
                      to={dialHref(c.id)}
                      aria-label={translate("gavion.dashboard.call_company", {
                        name: c.name,
                      })}
                    >
                      {translate("gavion.dashboard.call")}
                    </Link>
                  </Button>
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {offCrm ? (
        <OffCrmCallDialog
          companyId={Number(offCrm.id)}
          open
          onOpenChange={(open) => !open && setOffCrm(null)}
          onDone={() => {
            setOffCrm(null);
            notify("gavion.dialer.off_crm.done", { type: "success" });
            refresh();
          }}
          leadTimezone={offCrm.timezone}
          leadEmail={offCrm.email}
          now={() => new Date()}
        />
      ) : null}
    </section>
  );
};
